import { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppContext } from "@/App";
import { useTranslation } from "@/hooks/useTranslation";
import { useAssetConfig } from "@/hooks/useAssetConfig";
import api from "@/lib/api";
import {
  Truck, Power, Coffee, AlertTriangle, RefreshCw, CheckCircle2,
  X, Clock, Zap, MessageSquare, Hand, MapPin, Fuel,
  HardHat, ChevronRight, ArrowLeft, LogOut, Loader2,
  ShieldCheck, Send, ChevronDown, Users, BellRing, Droplet,
  ThumbsUp, ThumbsDown, Ban, Construction, TreePine, Waves,
  Wrench, Navigation, ArrowRightLeft, Settings, Heart, BookOpen,
  CalendarDays, Route, Bell, FileText
} from "lucide-react";
import TutorialLedger, { TutorialToggle } from "@/components/shared/TutorialLedger";
import DashboardSwitcher from "@/components/shared/DashboardSwitcher";
import LanguageSwitcher from "@/components/shared/LanguageSwitcher";
import PigeonFAB from "@/components/shared/PigeonFAB";
import LiveMap from "@/components/shared/LiveMap";
import { FeedbackModal, DeliveryModal, DriverSwitchModal, MessagesPanel } from "./DriverOverlays";
import { getMachineHighlight, getSiteHighlight, highlightBorder, highlightIcon, highlightMachineBadge } from "@/lib/highlights";

const CHECKLIST_ITEMS = [
  { key: "mirrors", icon: ShieldCheck },
  { key: "tires", icon: ShieldCheck },
  { key: "fuel_check", icon: Fuel },
  { key: "documents", icon: ShieldCheck },
  { key: "safety_gear", icon: ShieldCheck },
];

export default function DriverDashboard() {
  const { session, setSession, hubData, refreshData, logout } = useAppContext();
  const { t } = useTranslation(session?.lang);
  const ac = useAssetConfig(session);
  const nav = useNavigate();
  const { driverName: paramDriver } = useParams();
  const driverName = paramDriver || session?.driverName || "Driver";

  const { sites, machines, drivers, requests, events, messages, feedback } = hubData;
  const driver = drivers.find(d => d.driver_name === driverName);
  const isActive = driver?.status === "ACTIVE";

  const [toast, setToast] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [checklist, setChecklist] = useState({});
  const [showMessages, setShowMessages] = useState(false);
  const [deliveryModal, setDeliveryModal] = useState(null);
  const [dieselAmount, setDieselAmount] = useState(200);
  const [adblueAmount, setAdblueAmount] = useState(0);
  const [showDriverSwitch, setShowDriverSwitch] = useState(false);

  // Feedback system state
  const [feedbackModal, setFeedbackModal] = useState(null); // {site_id, site_name, machine_id, machine_number, request_id}
  const [feedbackMode, setFeedbackMode] = useState(null); // "positive" | "negative"
  const [selectedIncident, setSelectedIncident] = useState("");
  const [feedbackNotes, setFeedbackNotes] = useState("");
  const [positiveMessage, setPositiveMessage] = useState("");
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showMapPanel, setShowMapPanel] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calNotifCount, setCalNotifCount] = useState(0);
  const [myRoute, setMyRoute] = useState(null);
  const [calForm, setCalForm] = useState({ event_type: "LEAVE_REQUEST", title: "", date: "", description: "" });

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };
  const allChecked = CHECKLIST_ITEMS.every(item => checklist[item.key]);

  const myMessages = useMemo(() =>
    messages.filter(m => m.to_user === driverName || m.from_user === driverName)
  , [messages, driverName]);
  const unreadCount = myMessages.filter(m => m.to_user === driverName && !m.read).length;

  const myRequests = useMemo(() =>
    requests.filter(r => r.assigned_driver === driverName && r.status !== "DONE")
  , [requests, driverName]);

  const mySites = useMemo(() =>
    sites.filter(s =>
      s.main_driver === driverName ||
      (s.assigned_drivers || []).includes(driverName) ||
      myRequests.some(r => r.site_id === s.id)
    )
  , [sites, driverName, myRequests]);

  // Site feedback for the log
  const siteFeedback = useMemo(() => {
    const map = {};
    (feedback || []).forEach(fb => {
      if (!map[fb.site_id]) map[fb.site_id] = [];
      map[fb.site_id].push(fb);
    });
    return map;
  }, [feedback]);

  // Fetch calendar events and routes
  const loadCalendarAndRoutes = async () => {
    try {
      const [cal, rts] = await Promise.all([
        api.getCalendarEvents(session.hubId),
        api.getRoutes(session.hubId),
      ]);
      setCalendarEvents(cal);
      const pending = cal.filter(e => (e.event_type === "LEAVE_REQUEST" && e.assigned_to === driverName && e.status !== "PENDING") || (e.assigned_to === driverName) || (e.event_type === "TRAINING"));
      const newItems = cal.filter(e => new Date(e.created_at) > new Date(Date.now() - 24 * 60 * 60 * 1000));
      setCalNotifCount(newItems.length);
      const active = rts.find(r => r.driver_name === driverName && r.status === "ACTIVE");
      setMyRoute(active || null);
    } catch (e) { void 0; }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadCalendarAndRoutes(); }, []);

  const sessionHours = useMemo(() => {
    if (!isActive || !driver?.sessions?.length) return null;
    const last = driver.sessions[driver.sessions.length - 1];
    if (last.end) return null;
    const diff = Date.now() - new Date(last.start).getTime();
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    return `${h}h${String(m).padStart(2, "0")}`;
  }, [isActive, driver]);

  const handleShiftToggle = async () => {
    await api.toggleShift(driverName, session.hubId, isActive ? "stop" : "start");
    showToast(isActive ? t("stop_day") : t("start_day"));
    refreshData();
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshData();
    showToast(t("data_synced"));
    setIsRefreshing(false);
  };

  const handleBreak = async () => {
    await api.createEvent({ hub_id: session.hubId, event_type: "DRIVER_BREAK", driver_name: driverName, message: `${driverName} taking a break` });
    showToast(t("pause"));
    refreshData();
  };

  const handleBreakdown = async () => {
    await api.createEvent({ hub_id: session.hubId, event_type: "DRIVER_BREAKDOWN", driver_name: driverName, message: `${driverName} reported a breakdown` });
    showToast(t("breakdown") + "!");
    refreshData();
  };

  const handleDelivery = async () => {
    if (!deliveryModal) return;
    await api.confirmDelivery({
      machine_id: deliveryModal.machine_id, site_id: deliveryModal.site_id,
      diesel: dieselAmount, adblue: adblueAmount, driver_name: driverName
    });
    showToast(t("confirm_delivery") + "!");
    // After delivery, open feedback modal for this site
    const fmData = { ...deliveryModal };
    setDeliveryModal(null);
    setDieselAmount(200);
    setAdblueAmount(0);
    refreshData();
    // Open feedback prompt
    setFeedbackModal(fmData);
    setFeedbackMode(null);
    setSelectedIncident("");
    setFeedbackNotes("");
    setPositiveMessage("");
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackModal) return;
    setSubmittingFeedback(true);
    const isNegative = feedbackMode === "negative";
    const message = isNegative
      ? `${selectedIncident.replace("_", " ")}${feedbackNotes ? ": " + feedbackNotes : ""}`
      : positiveMessage || "All good - delivery successful!";

    await api.sendFeedback({
      hub_id: session.hubId,
      site_id: feedbackModal.site_id,
      site_name: feedbackModal.site_name,
      machine_id: feedbackModal.machine_id || "",
      machine_number: feedbackModal.machine_number || "",
      message,
      author: driverName,
      feedback_type: isNegative ? "NEGATIVE" : "POSITIVE",
      incident_type: isNegative ? selectedIncident : "",
    });
    showToast(isNegative ? "Incident reported!" : "Positive feedback sent!");
    setFeedbackModal(null);
    setSubmittingFeedback(false);
    refreshData();
  };

  const handleQuickPositive = async (siteId, siteName) => {
    await api.sendFeedback({
      hub_id: session.hubId, site_id: siteId, site_name: siteName,
      message: "Delivery completed successfully!", author: driverName,
      feedback_type: "POSITIVE", incident_type: "",
    });
    showToast("Positive feedback sent!");
    refreshData();
  };

  const openSiteFeedback = (site) => {
    setFeedbackModal({ site_id: site.id, site_name: site.name });
    setFeedbackMode(null);
    setSelectedIncident("");
    setFeedbackNotes("");
    setPositiveMessage("");
  };

  const handleHighFive = async (to) => {
    await api.sendHighFive({ from_user: driverName, to_user: to, hub_id: session.hubId });
    showToast(t("high_five_sent"));
    refreshData();
  };

  const handleMarkRead = async (msgId) => {
    await api.markRead(msgId);
    refreshData();
  };

  const handleSwitchDriver = (name) => {
    setShowDriverSwitch(false);
    nav(`/driver/${name}`);
  };

  const getReqTypeLabel = (req) => {
    const t = req.request_type || "FUEL";
    if (t === "FUEL") return { label: "FUEL", color: "bg-lime-50 text-lime-600", Icon: Fuel };
    if (t === "REPARATION") return { label: "REPAIR", color: "bg-red-500/10 text-red-400", Icon: Wrench };
    if (t === "MAINTENANCE") return { label: "MAINT", color: "bg-purple-500/10 text-purple-400", Icon: Settings };
    if (t === "TRANSPORT") return { label: "TRANSPORT", color: "bg-blue-500/10 text-blue-400", Icon: ArrowRightLeft };
    return { label: t, color: "bg-slate-100 text-slate-400", Icon: Zap };
  };

  return (
    <div className="max-w-lg mx-auto min-h-[100dvh] bg-white pb-32 relative" data-testid="driver-dashboard">
      {/* Toast */}
      {toast && (
        <div className="fixed top-5 left-4 right-4 z-[1000] bg-lime-500 text-slate-950 px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 font-bold text-sm uppercase tracking-wider animate-slideInDown max-w-lg mx-auto">
          <CheckCircle2 className="w-4 h-4" /> {toast}
        </div>
      )}

      <TutorialLedger role="driver" isOpen={showTutorial} onClose={() => setShowTutorial(false)} />

      <FeedbackModal modal={feedbackModal} onClose={() => setFeedbackModal(null)}
        mode={feedbackMode} setMode={setFeedbackMode}
        selectedIncident={selectedIncident} setSelectedIncident={setSelectedIncident}
        notes={feedbackNotes} setNotes={setFeedbackNotes}
        positiveMsg={positiveMessage} setPositiveMsg={setPositiveMessage}
        submitting={submittingFeedback} onSubmit={handleSubmitFeedback} />

      <DeliveryModal modal={deliveryModal} onClose={() => setDeliveryModal(null)}
        ac={ac} diesel={dieselAmount} setDiesel={setDieselAmount}
        adblue={adblueAmount} setAdblue={setAdblueAmount} onConfirm={handleDelivery} t={t} />

      <DriverSwitchModal show={showDriverSwitch} onClose={() => setShowDriverSwitch(false)}
        drivers={drivers} currentName={driverName} onSwitch={handleSwitchDriver} t={t} />

      <MessagesPanel show={showMessages} onClose={() => setShowMessages(false)}
        messages={myMessages} driverName={driverName} onMarkRead={handleMarkRead}
        driver={driver} onHighFive={handleHighFive} t={t} />

      {/* Header */}
      <header className="bg-white p-4 border-b border-slate-200 sticky top-0 z-50 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center text-white shadow-[0_0_15px_rgba(220,38,38,0.3)]">
            <Truck className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div>
            <h1 className="text-lg font-black tracking-tighter uppercase text-slate-900">{t("driver_dashboard")}</h1>
            <p className="text-[10px] font-bold tracking-widest uppercase text-slate-400">{driverName}</p>
          </div>
          <DashboardSwitcher currentRole="driver" />
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="driver-messages-btn" onClick={() => setShowMessages(true)}
            className={`relative p-2.5 rounded-xl transition-all border ${unreadCount > 0 ? "bg-lime-50 border-lime-300 text-lime-600" : "bg-slate-100 border-slate-200 text-slate-400"}`}>
            <MessageSquare className="w-4 h-4" />
            {unreadCount > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 text-white text-[8px] font-black flex items-center justify-center rounded-full">{unreadCount}</span>}
          </button>
          <TutorialToggle onClick={() => setShowTutorial(!showTutorial)} />
          <button data-testid="driver-calendar-btn" onClick={() => { setShowCalendar(!showCalendar); if (!showCalendar) loadCalendarAndRoutes(); }}
            className={`relative p-2.5 rounded-xl transition-all border ${showCalendar ? "bg-blue-600 border-blue-500 text-white" : calNotifCount > 0 ? "bg-slate-100 border-red-500/40 text-slate-400" : "bg-slate-100 border-slate-200 text-slate-400"}`}>
            <CalendarDays className="w-4 h-4" />
            {calNotifCount > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 text-white text-[8px] font-black flex items-center justify-center rounded-full">{calNotifCount}</span>}
          </button>
          <button data-testid="driver-map-btn" onClick={() => setShowMapPanel(!showMapPanel)}
            className={`p-2.5 rounded-xl transition-all border ${showMapPanel ? "bg-blue-600 border-blue-500 text-white" : "bg-slate-100 border-slate-200 text-slate-400 hover:text-slate-900"}`}>
            <MapPin className="w-4 h-4" />
          </button>
          <button data-testid="driver-switch-btn" onClick={() => setShowDriverSwitch(true)}
            className="p-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-400 hover:text-slate-900 transition-all flex items-center gap-1">
            <Users className="w-4 h-4" /><ChevronDown className="w-3 h-3" />
          </button>
          <button data-testid="driver-shift-btn" onClick={handleShiftToggle}
            className={`p-2.5 rounded-xl transition-all border ${isActive ? "bg-red-600 border-red-500 text-white shadow-[0_0_15px_rgba(220,38,38,0.3)]" : "bg-slate-100 border-slate-200 text-slate-400"}`}>
            <Power className="w-4 h-4" />
          </button>
          <LanguageSwitcher compact />
          <button data-testid="back-to-lobby-btn" onClick={() => { setSession(null); nav("/portal"); }}
            className="p-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-400 hover:text-slate-900 transition-all">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <button data-testid="driver-exit-btn" onClick={() => { logout(); nav("/"); }}
            className="p-2.5 bg-red-50 border border-red-200 rounded-xl text-red-500 hover:bg-red-600 hover:text-slate-900 transition-all">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Local Map */}
      {showMapPanel && (
        <div className="px-4 py-4 bg-slate-50/80 border-b border-slate-200 animate-slideInDown" data-testid="driver-map-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-blue-500" /> My Sites Map
            </h3>
            <button onClick={() => setShowMapPanel(false)} className="text-slate-400 hover:text-slate-900"><X className="w-4 h-4" /></button>
          </div>
          <div className="rounded-xl overflow-hidden border border-slate-200 shadow-sm">
            <LiveMap sites={mySites} machines={machines} requests={requests} />
          </div>
        </div>
      )}

      {/* Calendar Panel */}
      {showCalendar && (
        <div className="bg-slate-50/90 border-b border-blue-500/20 px-4 py-4 animate-slideInDown" data-testid="driver-calendar-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2"><CalendarDays className="w-4 h-4 text-blue-500" /> Calendar</h3>
            <button onClick={() => setShowCalendar(false)} className="text-slate-400 hover:text-slate-400"><X className="w-4 h-4" /></button>
          </div>
          {/* Leave Request Form */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <input value={calForm.title} onChange={e => setCalForm(p => ({ ...p, title: e.target.value }))} placeholder="Leave reason"
              className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-400 col-span-1" data-testid="driver-leave-title" />
            <input type="date" value={calForm.date} onChange={e => setCalForm(p => ({ ...p, date: e.target.value }))}
              className="bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-400" data-testid="driver-leave-date" />
            <button onClick={async () => {
              if (!calForm.title || !calForm.date) return;
              await api.createCalendarEvent({ hub_id: session.hubId, event_type: "LEAVE_REQUEST", title: calForm.title, date: calForm.date, created_by: driverName, assigned_to: driverName });
              setCalForm({ event_type: "LEAVE_REQUEST", title: "", date: "", description: "" });
              loadCalendarAndRoutes();
            }} disabled={!calForm.title || !calForm.date} data-testid="driver-leave-submit"
              className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white rounded-xl px-3 py-2 text-xs font-bold uppercase">Request Leave</button>
          </div>
          {/* Events list */}
          <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
            {calendarEvents.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-4">No events</p>
            ) : calendarEvents.map(evt => (
              <div key={evt.id} className={`p-2.5 rounded-xl border flex items-center justify-between ${
                evt.event_type === "LEAVE_REQUEST" && evt.status === "APPROVED" ? "border-lime-500/20 bg-lime-500/5" :
                evt.event_type === "LEAVE_REQUEST" && evt.status === "REJECTED" ? "border-red-500/20 bg-red-500/5" :
                evt.event_type === "TRAINING" ? "border-blue-500/20 bg-blue-500/5" :
                "border-slate-200 bg-white"
              }`}>
                <div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                      evt.event_type === "LEAVE_REQUEST" ? "bg-red-500/10 text-red-400" :
                      evt.event_type === "TOOLBOX" ? "bg-amber-50 text-amber-400" :
                      evt.event_type === "TRAINING" ? "bg-blue-500/10 text-blue-400" : "bg-slate-100 text-slate-400"
                    }`}>{evt.event_type}</span>
                    <span className="text-xs font-bold text-slate-800">{evt.title}</span>
                    {evt.status && evt.event_type === "LEAVE_REQUEST" && (
                      <span className={`text-[9px] font-bold ${evt.status === "APPROVED" ? "text-lime-600" : evt.status === "REJECTED" ? "text-red-400" : "text-amber-400 animate-pulse"}`}>{evt.status}</span>
                    )}
                  </div>
                  <span className="text-[10px] text-slate-400 font-mono">{evt.date}</span>
                </div>
                {evt.file_name && <span className="text-[9px] text-amber-400"><FileText className="w-3 h-3" /></span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <main className="p-4 space-y-5">
        {/* Assigned Route */}
        {myRoute && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="assigned-route">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-bold text-amber-400 uppercase tracking-widest flex items-center gap-2">
                <Route className="w-4 h-4" /> Assigned Route: {myRoute.name || "Today's Route"}
              </h3>
              <span className="text-[10px] text-slate-400">{myRoute.site_ids?.length || 0} sites</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {myRoute.site_ids?.map(sId => {
                const s = sites.find(x => x.id === sId);
                const routeSiteHL = s ? getSiteHighlight(s, machines, requests) : null;
                return (
                  <span key={sId} className={`text-[10px] font-bold px-2.5 py-1.5 rounded border flex items-center gap-1 ${
                    routeSiteHL === "red" ? "border-red-500/40 bg-red-500/10 text-red-400" :
                    routeSiteHL === "green" ? "border-lime-400 bg-lime-50 text-lime-600" :
                    "border-slate-200 bg-white text-slate-400"
                  }`}>
                    <MapPin className="w-3 h-3" />
                    {s?.name || sId}
                    {routeSiteHL === "red" && <Fuel className="w-3 h-3" />}
                    {routeSiteHL === "green" && <CheckCircle2 className="w-3 h-3" />}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Shift Status */}
        <div className={`p-5 rounded-xl border transition-all ${isActive ? "bg-slate-50 border-lime-500/20" : "bg-slate-50 border-slate-200 opacity-70"}`} data-testid="shift-status">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-xl ${isActive ? "bg-lime-50 text-lime-600" : "bg-slate-100 text-slate-400"}`}>
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900 uppercase tracking-tight">{t("day_status")}</h3>
                <p className={`text-[10px] font-bold ${isActive ? "text-lime-600" : "text-slate-400"}`}>
                  {isActive ? `${t("active")} ${sessionHours ? `/ ${sessionHours}` : ""}` : t("offline")}
                </p>
              </div>
            </div>
            {isActive && <div className="status-dot online" />}
          </div>
          <button data-testid="toggle-shift-btn" onClick={handleShiftToggle}
            className={`btn-industrial w-full py-4 rounded-xl text-sm font-black ${isActive ? "bg-red-600 text-white" : "bg-lime-500 text-slate-950 shadow-md"}`}>
            <Power className="w-5 h-5" /> {isActive ? t("stop_day") : t("start_day")}
          </button>
        </div>

        {/* Quick Actions */}
        {isActive && (
          <div className="grid grid-cols-2 gap-3" data-testid="quick-actions">
            <button data-testid="break-btn" onClick={handleBreak}
              className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex flex-col items-center gap-2 hover:bg-amber-100 transition-all active:scale-95">
              <Coffee className="w-6 h-6 text-amber-500" />
              <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">{t("pause")}</span>
            </button>
            <button data-testid="breakdown-btn" onClick={handleBreakdown}
              className="p-4 bg-red-50 border border-red-200 rounded-xl flex flex-col items-center gap-2 hover:bg-red-600/20 transition-all active:scale-95">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <span className="text-[10px] font-bold text-red-400 uppercase tracking-wider">{t("breakdown")}</span>
            </button>
          </div>
        )}

        {/* Checklist */}
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-5" data-testid="checklist">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-4">
            <ShieldCheck className="w-4 h-4 text-lime-500" /> {t("checklist")}
          </h3>
          <div className="space-y-2">
            {CHECKLIST_ITEMS.map(item => (
              <button key={item.key} onClick={() => setChecklist(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                data-testid={`check-${item.key}`}
                className={`w-full p-3 rounded-xl flex items-center justify-between border transition-all ${checklist[item.key] ? "bg-lime-50 border-lime-300 text-lime-600" : "bg-white border-slate-200 text-slate-400"}`}>
                <span className="text-xs font-bold uppercase tracking-wider">{t(item.key)}</span>
                {checklist[item.key] ? <CheckCircle2 className="w-4 h-4 text-lime-600" /> : <div className="w-4 h-4 rounded border border-slate-300" />}
              </button>
            ))}
          </div>
        </div>

        {/* Planning / Requests with Feedback */}
        <div data-testid="planning-section">
          <div className="flex items-center justify-between mb-3 px-1">
            <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">{t("my_planning")}</h3>
            <button onClick={handleRefresh} className={`text-slate-400 hover:text-lime-600 ${isRefreshing ? "animate-spin-slow" : ""}`}>
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {mySites.length === 0 ? (
            <div className="py-16 text-center border border-dashed border-slate-200 rounded-xl">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">{t("no_planning")}</p>
            </div>
          ) : mySites.map(site => {
            const siteReqs = myRequests.filter(r => r.site_id === site.id);
            const sfb = (siteFeedback[site.id] || []).slice(0, 5);
            const driverSiteHL = getSiteHighlight(site, machines, requests);
            return (
              <div key={site.id} className={`bg-slate-50 border rounded-xl p-4 mb-3 ${driverSiteHL ? highlightBorder(driverSiteHL) : "border-slate-200"}`} data-testid={`driver-site-${site.id}`}>
                <div className="flex items-center gap-3 mb-3">
                  <MapPin className={`w-4 h-4 ${driverSiteHL === "red" ? "text-red-500" : driverSiteHL === "green" ? "text-lime-600" : "text-red-500"}`} />
                  <div className="flex-1">
                    <h4 className="text-sm font-bold text-slate-900 uppercase tracking-tight">{site.name}</h4>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">{site.city}</span>
                      {site.current_stage && <span className="text-[9px] font-bold bg-blue-500/10 text-blue-400 px-1.5 py-0.5 rounded">{site.current_stage}</span>}
                      {site.gps_lat > 0 && (
                        <a href={`https://www.google.com/maps?q=${site.gps_lat},${site.gps_lon}`} target="_blank" rel="noopener noreferrer"
                          className="text-[9px] text-lime-500 font-mono flex items-center gap-0.5 hover:underline">
                          <Navigation className="w-2.5 h-2.5" /> GPS
                        </a>
                      )}
                    </div>
                  </div>
                  {/* Site feedback button */}
                  <button data-testid={`site-feedback-btn-${site.id}`} onClick={() => openSiteFeedback(site)}
                    className="px-3 py-2 bg-slate-100 border border-slate-200 rounded-xl text-[10px] font-bold text-slate-400 uppercase hover:text-slate-900 hover:border-slate-300 transition-all flex items-center gap-1.5">
                    <MessageSquare className="w-3.5 h-3.5" /> Log
                  </button>
                </div>

                {/* Access info */}
                {(site.lock_codes || site.accessibility_route) && (
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {site.lock_codes && (
                      <span className="text-[10px] font-bold bg-amber-50 text-amber-400 px-2 py-1 rounded">Code: {site.lock_codes}</span>
                    )}
                    {site.accessibility_route && (
                      <span className="text-[10px] font-bold bg-blue-500/10 text-blue-400 px-2 py-1 rounded truncate max-w-[200px]">Route: {site.accessibility_route}</span>
                    )}
                  </div>
                )}

                {/* Machine status on site */}
                {(() => {
                  const sMachines = machines.filter(m => m.site_id === site.id);
                  const statusMachines = sMachines.filter(m => m.status_type && m.status_type !== "NORMAL");
                  if (statusMachines.length === 0 && sMachines.length === 0) return null;
                  return (
                    <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`driver-machines-${site.id}`}>
                      {sMachines.map(m => {
                        const dMHL = getMachineHighlight(m, requests);
                        return (
                          <span key={m.id} className={`text-[9px] font-bold px-1.5 py-1 rounded border flex items-center gap-0.5 ${
                            dMHL ? highlightMachineBadge(dMHL) :
                            m.status_type === "DEFECT" ? "border-red-500/30 bg-red-500/10 text-red-400" :
                            m.status_type === "TRANSPORT" ? "border-blue-500/30 bg-blue-500/10 text-blue-400" :
                            m.status_type === "UPKEEP" ? "border-purple-500/30 bg-purple-500/10 text-purple-400" :
                            "border-slate-200 bg-white text-slate-400"
                          }`}>
                            {dMHL === "red" && <Fuel className="w-2.5 h-2.5" />}
                            {dMHL === "green" && <CheckCircle2 className="w-2.5 h-2.5" />}
                            {!dMHL && m.status_type === "DEFECT" && <Wrench className="w-2.5 h-2.5" />}
                            {!dMHL && m.status_type === "TRANSPORT" && <ArrowRightLeft className="w-2.5 h-2.5" />}
                            {!dMHL && m.status_type === "UPKEEP" && <Settings className="w-2.5 h-2.5" />}
                            {m.machine_number}
                          </span>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* Requests */}
                {siteReqs.map(req => {
                  const rt = getReqTypeLabel(req);
                  const Icon = rt.Icon;
                  const isTransport = req.request_type === "TRANSPORT";
                  return (
                    <div key={req.id} className={`p-3 border rounded-xl mb-2 flex items-center justify-between ${
                      isTransport ? "border-blue-500/30 bg-blue-500/5" :
                      req.urgency === "URGENT" ? "border-red-600/30 bg-red-600/5" : "border-slate-200 bg-white"
                    }`} data-testid={`driver-request-${req.id}`}>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-800">{req.machine_number}</span>
                          <span className={`text-[9px] font-bold ${rt.color} px-1.5 py-0.5 rounded flex items-center gap-0.5`}>
                            <Icon className="w-2.5 h-2.5" /> {rt.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          {!isTransport && req.products?.length > 0 && req.products.map((p, i) => (
                            <span key={i} className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${i === 0 ? "bg-slate-100 text-slate-400" : "bg-sky-500/10 text-sky-400"}`}>{p}</span>
                          ))}
                          {req.urgency === "URGENT" && <span className="text-[9px] font-bold bg-red-50 text-red-400 px-1.5 py-0.5 rounded animate-pulse">URGENT</span>}
                        </div>
                        {req.is_fun_request && <p className="text-[11px] text-amber-400 italic mt-1">{req.fun_message}</p>}
                        {isTransport && req.target_site_name && (
                          <div className="mt-1.5 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-[10px] text-blue-600 font-bold flex items-center gap-1"><ArrowRightLeft className="w-3 h-3" /> Transport to: {req.target_site_name}</p>
                            {req.notes && <p className="text-[9px] text-blue-400 mt-0.5">{req.notes}</p>}
                          </div>
                        )}
                        {!isTransport && req.target_site_name && <p className="text-[10px] text-blue-400 mt-1 flex items-center gap-1"><ArrowRightLeft className="w-3 h-3" /> To: {req.target_site_name}</p>}
                      </div>
                      {isTransport ? (
                        <button data-testid={`complete-transport-${req.id}`}
                          onClick={async () => {
                            await api.confirmTransport(req.id);
                            showToast("Transport completed! Machine relocated.");
                            refreshData();
                          }}
                          disabled={!isActive || !allChecked}
                          className="px-4 py-2 bg-blue-600 text-white rounded-xl text-xs font-bold uppercase disabled:opacity-30 active:scale-95 transition-all flex items-center gap-1">
                          <Truck className="w-3.5 h-3.5" /> Complete
                        </button>
                      ) : (
                        <button data-testid={`deliver-${req.id}`}
                          onClick={() => setDeliveryModal({ machine_id: req.machine_id, site_id: req.site_id, machine_number: req.machine_number, site_name: site.name })}
                          disabled={!isActive || !allChecked}
                          className="px-4 py-2 bg-lime-500 text-slate-950 rounded-xl text-xs font-bold uppercase disabled:opacity-30 active:scale-95 transition-all flex items-center gap-1">
                          <Fuel className="w-3.5 h-3.5" /> {t("deliver")}
                        </button>
                      )}
                    </div>
                  );
                })}

                {/* Quick feedback buttons (no delivery pending) */}
                {siteReqs.length === 0 && (
                  <div className="flex gap-2">
                    <button data-testid={`quick-positive-${site.id}`} onClick={() => handleQuickPositive(site.id, site.name)}
                      className="flex-1 py-2.5 bg-lime-50 border border-lime-300 rounded-xl text-[10px] font-bold text-lime-600 uppercase flex items-center justify-center gap-1 hover:bg-lime-100 active:scale-95 transition-all">
                      <ThumbsUp className="w-3 h-3" /> All Good
                    </button>
                    <button data-testid={`quick-negative-${site.id}`} onClick={() => { openSiteFeedback(site); setFeedbackMode("negative"); }}
                      className="flex-1 py-2.5 bg-red-500/10 border border-red-500/30 rounded-xl text-[10px] font-bold text-red-400 uppercase flex items-center justify-center gap-1 hover:bg-red-500/20 active:scale-95 transition-all">
                      <ThumbsDown className="w-3 h-3" /> Problem
                    </button>
                  </div>
                )}

                {/* Recent site feedback log */}
                {sfb.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-200 space-y-1.5" data-testid={`feedback-log-${site.id}`}>
                    <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">Recent Site Log</p>
                    {sfb.map(fb => (
                      <div key={fb.id} className={`p-2.5 rounded border text-[11px] font-bold ${
                        fb.feedback_type === "NEGATIVE"
                          ? "border-red-500/30 bg-red-500/5 text-red-400"
                          : "border-lime-300 bg-lime-500/5 text-lime-600"
                      }`} data-testid={`feedback-entry-${fb.id}`}>
                        <div className="flex items-center justify-between">
                          <span>{fb.feedback_type === "NEGATIVE" ? <AlertTriangle className="w-3 h-3 inline mr-1" /> : <ThumbsUp className="w-3 h-3 inline mr-1" />}{fb.author}</span>
                          <span className="text-[9px] text-slate-400">{new Date(fb.date).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</span>
                        </div>
                        <p className={`mt-0.5 ${fb.feedback_type === "NEGATIVE" ? "text-red-300" : "text-lime-300"}`}>{fb.message}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>

      <PigeonFAB role="driver" events={events} />
    </div>
  );
}
